// src/routes/core/countryGate.ts
// Layer 2 of the Country Gate: routing enforcement. UK, Canada, Germany,
// Australia, New Zealand, and Ireland ship fully functional — every one of
// their real page routes must render, not redirect.
//
// Country-specific routes live in two places: their own domain files
// (src/routes/domains/{canada,australia,germany,newzealand,ireland}/*)
// and, for ~350 of them, intermixed directly into core.routes.tsx
// alongside UK and country-agnostic routes. A per-domain filter can't
// catch the ones in `core`, so this matches on path prefix instead,
// derived from an exhaustive read of every country-tagged route
// currently in the repo (2026-09-05) — not an assumption from naming
// convention alone.
import { Navigate } from 'react-router-dom';
import type { AppRoute } from './types';

export const COUNTRY_GATE_PATH = '/countries';

// One entry per real path-prefix pattern found in the actual route
// declarations. Both slash and hyphen variants exist for the same
// country (e.g. /canada-worker-assessment and /canada/pnp-finder) and
// both abbreviated and full forms exist for two countries (/au-* and
// /australia-*; /de-* and /germany-*; /nz-* and /new-zealand-*).
const BLOCKED_COUNTRY_PATH_PREFIXES: string[] = [
  // Canada OPEN — /canada-* and /canada/
  // Germany OPEN — /germany-* , /de-* and /de/
  // Australia OPEN — /au-* , /au/ and /australia-*
  // New Zealand OPEN — /nz-* , /nz/ and /new-zealand-*
  // Ireland OPEN — /ireland-*
];

// Routes that carry real country-specific content but don't follow any
// of the prefixes above — found by cross-checking every Australia/
// NewZealand/Ireland-named component against its registered path.
const EXPLICIT_BLOCKED_PATHS: string[] = [];

export function isBlockedCountryPath(path: string | undefined): boolean {
  if (!path) return false;
  if (EXPLICIT_BLOCKED_PATHS.includes(path)) return true;
  return BLOCKED_COUNTRY_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// Applied once to the fully merged route table (src/routes/index.tsx),
// after every domain has loaded — this is deliberately the single point
// of enforcement rather than a per-domain pass, since the blocked paths
// are scattered across six different domain files. Recurses into
// `children` so nested routes are covered too.
export function gateNonUkCountryRoutes(routes: AppRoute[]): AppRoute[] {
  return routes.map((route) => {
    if (isBlockedCountryPath(route.path)) {
      return {
        ...route,
        element: <Navigate to={COUNTRY_GATE_PATH} replace />,
        children: undefined,
      };
    }
    if (route.children) {
      return { ...route, children: gateNonUkCountryRoutes(route.children) };
    }
    return route;
  });
}
